import * as Config from '../config';
import { useState, useCallback, useReducer, useEffect, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { setStorePlaylists, setExtraPlaylists, addExtraPlaylists, setPlaylistTracksAdded } from "../actions/index";

const playlistsReducer = (state, action) => {
  switch(action.type) {
    case 'new':
      return [{
        id: action.id,
        name: action.name,
        tracks: [...action.data],
        track_count: action.data.length,
        release_images: [...action.data.map(x => (x && x.img) ? x.img : Config.DEFAULT_IMG)],
        description: null,
        is_owner: true,
        length_ms: 0,
        ...action.extra
      }, ...state]
    case 'add':
      return [ ...state, action.playlist]
    case 'extra':
      return [ ...state, ...action.data]
    case 'update':
      const updated = [...state];
      let playlist = updated.find( i => i.id === action.id );
      if (playlist && playlist.hasOwnProperty(action.field)) {
        playlist[action.field] = action.data;
      }
      return updated;
    case 'increment':
      const pUpdated = [...state];
      let pInc = pUpdated.find( i => i.id === action.id );
      if (pInc && pInc[action.field]) {
        pInc[action.field] += action.data;
      }
      return pUpdated;
    case 'push':
      const xUpdated = [...state];
      let pPush = xUpdated.find( i => i.id === action.id );
      pPush[action.field] = [...pPush[action.field], ...action.data];
      return xUpdated;
    case 'remove':
      const index = state.findIndex( item => item.id === action.id );
      const removed = [...state];
      removed.splice(index, 1);
      return removed
    case 'clean-load':
      const cIndex = state.findIndex( item => item.load );
      const cRemoved = [...state];
      if (cIndex >= 0) { cRemoved.splice(cIndex, 1); }
      return cRemoved
    case `sortable`:
      const startIndex = state.findIndex( x => x.id === action.id );
      return [...Config.reorder(state, startIndex, action.endIndex)]
    case 'initial':
      return [...action.data]; // New array playlists
    case 'loading':
      return [];
    default:
      return state;
  }
}

const usePlaylists = (api, isConnected, user) => {
  const [playlists, dispatchPlaylists] = useReducer(playlistsReducer, []);
  const dispatch = useDispatch();
  const refreshLoadingRef = useRef(true);
  const [refresh, setRefresh] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const nextLoadRef = useRef(null);

  const getMore = useCallback((url, isFirst, uPlaylists = []) => {
    const request = (isFirst) ? api.getFromApi(url, false) : api.getFromURL(url);

    if (!isFirst) {
      dispatchPlaylists({ type: 'extra', data: [{ name: 'loading component', load: true }] })
    } else {
      setLoading(true);
    }

    request.then( res => {
      setLoading(false);
      dispatchPlaylists({
        type: /*(isFirst) ? `initial` :*/ `extra`,
        data: res.data.results.map( x => {

          // MERGE WITH CUSTOM DATA BDD
          const uPlaylist = uPlaylists.find(y => y.id === x.id);
          if (uPlaylist) {
            x.covers = uPlaylist.covers;
            x.fromDJ = uPlaylist.fromDJ;
          }

          x.tracks = [];
          return x;
        } )
      })
      dispatchPlaylists({type: 'clean-load', data:[]});
      nextLoadRef.current = res.data.next ? () => { getMore(res.data.next, false, uPlaylists) } : null;
    } );
  }, [api]);

  const loadMore = useCallback(() => {
    if (nextLoadRef.current) { nextLoadRef.current(); }
  }, [])

  const getTracks = useCallback((id) => {
    return api.getFromApi(`/v4/my/playlists/${id}/tracks/?per_page=100`, false);
  }, [api]);

  useEffect(() => {
    if (api && isConnected && user && refreshLoadingRef.current) { // All accounts
      refreshLoadingRef.current = false;
      api.getFromYD(`/uplaylists.php`, { userid: user.id, action: `retrieve` })
        .then( res => {
          dispatchPlaylists({type: `loading`});
          dispatch(setExtraPlaylists(res.data));
          getMore(`/v4/my/playlists/?per_page=100`, true, res.data);
        })
        .catch( e => {
          dispatchPlaylists({type: `loading`});
          getMore(`/v4/my/playlists/?per_page=100`, true);
        } );
    } else if (api && !isConnected) { // No Account
      dispatchPlaylists({ type: `initial`, data: [] });
    }
  }, [api, getMore, isConnected, user, dispatch, refresh]);

  const addTracksToPlaylistAPI = useCallback((id, track_ids) => {
    api.post(`/v4/my/playlists/${id}/tracks/bulk/`, { track_ids }).then( res => {
      dispatchPlaylists({type: `update`, id, field: `track_count`, data: res.data.playlist.track_count})
      dispatchPlaylists({type: `update`, id, field: `length_ms`, data: res.data.playlist.length_ms})
      dispatch(setPlaylistTracksAdded(id));
    })
  }, [api, dispatch]);

  //Middleware
  const newPlaylist = useCallback((name, data, extra = {}, next = null, action = null) => {
    let id = Date.now().toString();

    if (isConnected) { // All Accounts

      const moreCopy = (id, track_ids, next) => {
        addTracksToPlaylistAPI(id, track_ids);
        if (next) {
          const req = (next.includes(`https`)) ? api.getFromURL(next) : api.getFromApi(next, false);
          req.then( response => {
            dispatchPlaylists({type: `increment`, id, field: `track_count`, data: response.data.results.length})
            moreCopy(id, response.data.results.map(x => (x.track && x.track.id) ? x.track.id : x.id), response.data.next);
          })
        }
      }

      api.post(`/v4/my/playlists/`, { name }).then( response => {
        id = response.data.id;
        dispatchPlaylists({type: `new`, id, name, data, extra})
        moreCopy(id, data.map(x => x.id), next);

        if (Object.keys(extra).length > 0) { //IF FROM CHARTS ADD EXTRA
          dispatch(addExtraPlaylists( { id, ...extra } ));
          const dataToYD = { id, from: extra.id, ...extra }; //SAVE TO YD
          api.getFromYD(`/uplaylists.php`, { userid: user.id, action: `insert`, playlist: JSON.stringify(dataToYD) });
        }

        if (action) { action(id, name) }

      });

    } else { // Localstorage
      dispatchPlaylists({type: `new`, id, name, data, extra});
      if (action) { action(id) }
    }
    return id;
  }, [api, isConnected, addTracksToPlaylistAPI, dispatch, user]);

  const report = (scope, data) => {
    const dataEvent = data.map( x => ({ ...x, owner: user.name }) )
    api.postR(scope, dataEvent);
  }

  const onRefresh = (newKey) => {
    setRefresh(newKey);
    refreshLoadingRef.current = true;
  }

  const onSortable = (id, endIndex) => {
    dispatchPlaylists({type: `sortable`, id, endIndex})
    const startIndex = playlists.findIndex( x => x.id === id );
    let newOrderPlaylist = [...Config.reorder([...playlists], startIndex, endIndex)]
    const bulk = newOrderPlaylist.map( (x, index) => ({ 'position': index + 1, 'item_id': x.id /*, 'name': x.name*/ }))
    api.patch(`/v4/my/playlists/bulk/`, { items: bulk });
  }

  const store = {
    playlists,
    isLoading,
    get: (id) => (playlists) ? playlists.find(x => parseInt(x.id) === parseInt(id)) : null,
    new: newPlaylist,
    newLocal: (id, name, data, extra) => { setTimeout(() => { dispatchPlaylists({type: `new`, id, name, data, extra}); }, 500) }, // the timeout fix async
    add: (playlist) => dispatchPlaylists({type: `add`, playlist}),
    update: (id, field, data) => dispatchPlaylists({type: `update`, id, field, data}),
    increment: (id, field, data) => dispatchPlaylists({type: `increment`, id, field, data}),
    push: (id, field, data) => dispatchPlaylists({type: `push`, id, field, data}),
    remove: (id) => { setTimeout(() => { dispatchPlaylists({type: `remove`, id}) }, 500) },
    reset: (data) => dispatchPlaylists({type: `initial`, data}),
    // sortable: (id, endIndex) => dispatchPlaylists({type: `sortable`, id, endIndex}),
    sortable: (id, endIndex) => onSortable(id, endIndex),
    refresh: onRefresh,
    report: (scope, data) => report(scope, data),
    addTracksToPlaylistAPI,
    getTracks,
    loadMore,
  };

  useEffect(() => {
    dispatch(setStorePlaylists(store));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlists, isLoading, dispatch])

  // Side-channels (replace setTimeout reducer) — (concurrent-safe).
  const lastPlaylistUpdated     = useSelector(s => s.lastPlaylistUpdated);
  const lastPlaylistPushed      = useSelector(s => s.lastPlaylistPushed);
  const lastPlaylistIncremented = useSelector(s => s.lastPlaylistIncremented);
  const lastPlaylistSorted      = useSelector(s => s.lastPlaylistSorted);

  // `update` est idempotent → pas de garde nécessaire.
  useEffect(() => {
    if (lastPlaylistUpdated) store.update(lastPlaylistUpdated.id, lastPlaylistUpdated.property, lastPlaylistUpdated.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPlaylistUpdated]);

  // `push` / `increment` - anti-double
  const lastPushAt = useRef(null);
  useEffect(() => {
    if (lastPlaylistPushed && lastPlaylistPushed.at !== lastPushAt.current) {
      lastPushAt.current = lastPlaylistPushed.at;
      store.push(lastPlaylistPushed.id, lastPlaylistPushed.property, lastPlaylistPushed.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPlaylistPushed]);

  const lastIncAt = useRef(null);
  useEffect(() => {
    if (lastPlaylistIncremented && lastPlaylistIncremented.at !== lastIncAt.current) {
      lastIncAt.current = lastPlaylistIncremented.at;
      store.increment(lastPlaylistIncremented.id, lastPlaylistIncremented.property, lastPlaylistIncremented.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPlaylistIncremented]);

  // concurrent-safe
  const lastSortAt = useRef(null);
  useEffect(() => {
    if (lastPlaylistSorted && lastPlaylistSorted.at !== lastSortAt.current) {
      lastSortAt.current = lastPlaylistSorted.at;
      onSortable(lastPlaylistSorted.trackID, lastPlaylistSorted.endIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPlaylistSorted]);

  return store;
};

export default usePlaylists;
